import { useMemo, useState } from 'react';
import { boxOfficeList, type BoxOfficeMovie } from '@/mocks/cinema';

const genreOptions = ['全部类型', '科幻', '剧情', '动作', '动画', '悬疑', '爱情'];

const rankColors = ['bg-accent-500', 'bg-secondary-400', 'bg-secondary-300'];

function heatBadge(movie: BoxOfficeMovie) {
  const map: Record<BoxOfficeMovie['heat'], string> = {
    爆棚: 'bg-primary-600 text-background-50',
    火热: 'bg-primary-100 text-primary-700',
    上升: 'bg-accent-100 text-accent-800',
    平稳: 'bg-secondary-100 text-secondary-800',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${map[movie.heat]}`}>
      {movie.heat}
    </span>
  );
}

export default function BoxOffice() {
  const [genre, setGenre] = useState('全部类型');
  const [sortBy, setSortBy] = useState<'today' | 'heat'>('today');
  const [highlight, setHighlight] = useState<number | null>(null);

  const list = useMemo(() => {
    let arr = boxOfficeList.filter(
      (m) => genre === '全部类型' || m.genre.includes(genre),
    );
    if (sortBy === 'heat') {
      arr = [...arr].sort((a, b) => b.seatRate - a.seatRate);
    }
    return arr;
  }, [genre, sortBy]);

  return (
    <section id="boxoffice" className="bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-fire-fill"></i>
              实时票房榜
            </span>
            <h2 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">
              今日票房排行 TOP 10
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              按今日分时票房排序，实时呈现每部影片的票房、排片占比、上座率与热度变化，冲得快不快一眼便知。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-200 p-1">
              {genreOptions.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGenre(g)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    genre === g
                      ? 'bg-primary-600 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
              {(
                [
                  { key: 'today', label: '按票房' },
                  { key: 'heat', label: '按上座率' },
                ] as const
              ).map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setSortBy(o.key)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    sortBy === o.key
                      ? 'bg-foreground-900 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-3">
            {list.map((m, i) => (
              <div
                key={m.title}
                onMouseEnter={() => setHighlight(m.rank)}
                onMouseLeave={() => setHighlight(null)}
                className={`group flex items-center gap-3 rounded-lg border bg-background-50 p-3 transition-all md:p-4 ${
                  highlight === m.rank
                    ? 'border-primary-300'
                    : 'border-background-200'
                }`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-display text-lg font-bold ${
                    i < 3 ? `${rankColors[i]} text-background-50` : 'bg-background-100 text-foreground-500'
                  }`}
                >
                  {m.rank}
                </span>
                <img
                  src={m.img}
                  alt={`${m.title} 电影海报`}
                  className="h-20 w-14 shrink-0 rounded-md object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading text-base font-bold text-foreground-950">{m.title}</h3>
                    <span className="text-xs text-foreground-500">{m.genre}</span>
                    {heatBadge(m)}
                  </div>
                  <p className="mt-1 text-xs text-foreground-500">
                    {m.release}上映 · 累计 {m.totalBox}
                  </p>
                  <div className="mt-2 hidden items-center gap-3 text-xs text-foreground-600 sm:flex">
                    <span>
                      排片占比 <b className="font-medium text-foreground-900">{m.showRate}%</b>
                    </span>
                    <span>
                      上座率 <b className="font-medium text-foreground-900">{m.seatRate}%</b>
                    </span>
                    <span
                      className={`flex items-center gap-0.5 ${
                        m.trend >= 0 ? 'text-primary-600' : 'text-secondary-600'
                      }`}
                    >
                      <i className={m.trend >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
                      {Math.abs(m.trend)}%
                    </span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-display text-lg font-bold text-primary-600">
                    {m.todayBox.toLocaleString('zh-CN')}
                  </p>
                  <p className="text-xs text-foreground-500">今日票房·万元</p>
                  <a
                    href="#schedule"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('schedule')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="mt-1.5 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-700 opacity-0 transition-opacity hover:bg-primary-100 hover:text-primary-700 group-hover:opacity-100"
                  >
                    查场次
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-lg font-bold text-foreground-950">场次热度速览</h3>
              <p className="mt-1 text-xs text-foreground-600">近 3 小时各时段购票热度分布</p>
              <div className="mt-5 space-y-4">
                {[
                  { t: '上午 10:00-12:00', v: 32, d: '首批场次，票量充足' },
                  { t: '午后 13:00-16:00', v: 58, d: '亲子场与错峰观众集中' },
                  { t: '黄金场 18:00-21:00', v: 91, d: '全天最热，部分厅售罄' },
                  { t: '深夜 22:00 后', v: 41, d: '惊悚片与夜场影迷扎堆' },
                ].map((b) => (
                  <div key={b.t}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-foreground-800">{b.t}</span>
                      <span className="text-foreground-500">{b.d}</span>
                    </div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-background-200">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-accent-500 to-primary-500"
                        style={{ width: `${b.v}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
              <a
                href="#schedule"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('schedule')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="mt-6 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                查今晚黄金场次
                <i className="ri-time-line"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}